import { Paper, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenRoundedIcon from '@material-ui/icons/LockOpenRounded';
import SolidSmallIconButton from 'Components/Buttons/SolidSmallIconButton';
import useAsyncTask from 'hooks/useAsyncTask';
import useConfirmationDialog from 'hooks/useConfirmationDialog';
import useToastMessage from 'hooks/useToastMessage';
import { DefinitionsModel } from 'Models/Definitions';
import { Definition } from 'Models/Definitions/@types';
import { PictureModel } from 'Models/Pictures/Model';
import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { TDispatch } from 'Typings/Global';

export interface DefinitionCardProps {
    definition: Definition
}

const DefinitionCard: FC<DefinitionCardProps> = ({ definition }) => {
    const classes = useStyles();
    const pic = PictureModel.getPicUrl(definition.picture, 'sm');

    const withConfirmationDialog = useConfirmationDialog();

    const dispatch = useDispatch<TDispatch>();

    const togglePublishTask = useAsyncTask(async () => await dispatch(DefinitionsModel.postActivity(DefinitionsModel.Activities.TOGGLE_PUBLISH, {}, { id: definition?.id, publish: !definition?.isPublished })))

    const deleteTask = useAsyncTask(async () => await dispatch(DefinitionsModel.deleteItem(definition.id, {}, undefined, { definition })));

    const loading = togglePublishTask.status === "PROCESSING" || deleteTask.status === "PROCESSING";

    const withToast = useToastMessage();

    const togglePublish = () => { withToast(togglePublishTask.run, { successToastMessage: definition.isPublished ? "Unpublished successfully" : "Published successfully" }); }

    const handleDelete = () => {
        withConfirmationDialog(async () => {
            await deleteTask.run();
        }, { agreeText: 'Delete', cancelText: 'Cancel', message: 'Do you want to delete this book?' }, { successToastMessage: "Delete Success" })
    }

    return (
        <Paper className={classes.root}>
            <Link to={`/dashboard/definitions/${definition.slug}`} >
                <div className={classes.picContainer} >
                    {pic ? <img className={classes.pic} src={pic} alt={definition.title} /> : null}
                </div>
            </Link>
            <Link to={`/dashboard/definitions/${definition.slug}`} >
                <div className={classes.content} >
                    <Typography variant={'subtitle1'} >{definition.title}</Typography>
                    <Typography className={classes.description} variant={'overline'} color={'textSecondary'} >{definition.shortDescription}</Typography>
                </div>
            </Link>
            <div className={classes.optionsIcon} >
                <SolidSmallIconButton onClick={togglePublish} color={definition.isPublished ? undefined : 'contrast'} disabled={loading} >
                    {
                        definition.isPublished ?
                            <LockOpenRoundedIcon fontSize={'small'} />
                            :
                            <LockIcon color={'error'} fontSize={'small'} />
                    }
                </SolidSmallIconButton>
                <SolidSmallIconButton disabled={loading} onClick={handleDelete} ><ClearRoundedIcon fontSize={'small'} /></SolidSmallIconButton>
            </div>
        </Paper>
    )
}

const useStyles = makeStyles<Theme>((theme) => {
    const { palette: { secondary } } = theme;
    return (createStyles({
        root: {
            padding: 12,
            display: 'flex',
            width: 350,
            height: 150,
            position: 'relative'
        },
        picContainer: {
            height: 125, width: 125,
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: secondary['main'],
        },
        pic: {
            width: '100%',
            height: 'auto',
        },
        content: {
            marginLeft: 12,
            display: 'flex',
            flexDirection: 'column',
            flex: 1
        },
        description: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            '-webkit-line-clamp': 4,
            '-webkit-box-orient': 'vertical',
            lineHeight: '14px'
        },
        optionsIcon: {
            position: 'absolute',
            bottom: 12, right: 12
        }
    }))
})

export default DefinitionCard